import React from 'react';
import { useHiveCore } from '../../services/hivecore-scene-stream/HiveCoreProvider';
import { useActionFeedback } from '../../services/interaction/useActionFeedback';
import {
  Activity,
  Cpu,
  Database,
  CheckCircle,
  AlertCircle,
  Clock,
  Gauge,
  HardDrive,
  Server,
  Terminal
} from 'lucide-react';

export function HiveCoreScenePanel() {
  const { state, connected } = useHiveCore();
  const { systemMetrics, metricsLoading, metricsError } = state;
  const { state: widgetStates, showPending, fire, setState } = useActionFeedback();

  const metrics = systemMetrics;
  const cpuPercent = metrics ? state.cpu * 100 : null;
  const memoryPercent = typeof metrics?.memory?.usagePercent === 'number'
    ? metrics.memory.usagePercent
    : null;
  const fallbackMemoryPercent = !metrics && state.memory ? state.memory * 100 : null;
  const effectiveMemoryPercent = memoryPercent ?? fallbackMemoryPercent;

  const formatPercent = (value: number | null | undefined, digits = 1) => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '—';
    }
    return `${value.toFixed(digits)}%`;
  };

  const formatMegabytes = (value: number | null | undefined) => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '—';
    }
    return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} MB`;
  };

  const formatNumber = (value: number | null | undefined, maximumFractionDigits = 2) => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '—';
    }
    return value.toLocaleString(undefined, { maximumFractionDigits });
  };

  const formatUptime = (seconds: number | null | undefined) => {
    if (seconds === null || seconds === undefined || Number.isNaN(seconds)) {
      return '—';
    }
    const wholeSeconds = Math.max(0, Math.floor(seconds));
    const days = Math.floor(wholeSeconds / 86400);
    const hours = Math.floor((wholeSeconds % 86400) / 3600);
    const minutes = Math.floor((wholeSeconds % 3600) / 60);
    const parts: string[] = [];
    if (days) parts.push(`${days}d`);
    if (hours || days) parts.push(`${hours}h`);
    parts.push(`${minutes}m`);
    return parts.join(' ');
  };

  const lastSample = metrics ? new Date(metrics.timestamp * 1000) : null;

  const widgetKey = 'refresh.btn';
  const currentState = widgetStates[widgetKey] || 'idle';
  const showSpinner = showPending[widgetKey] || false;

  const handleRefresh = async () => {
    try {
      await fire(widgetKey, 'scene.refresh');
      if (navigator.vibrate && currentState === 'success') {
        navigator.vibrate(10);
      }
    } catch (error) {
      console.error('[HiveCoreScenePanel] Refresh failed:', error);
      if (navigator.vibrate) {
        navigator.vibrate(10);
      }
    }
  };

  const handlePointerDown = () => {
    setState(widgetKey, 'pressed');
  };

  const handlePointerUp = () => {
    if (currentState === 'pressed') {
      setState(widgetKey, 'idle');
    }
  };

  const handlePointerLeave = () => {
    if (currentState === 'pressed') {
      setState(widgetKey, 'idle');
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gradient-to-br from-[rgba(var(--arc-blue),0.80)] to-[rgba(var(--arc-navy),0.60)] backdrop-blur-xl rounded-xl border border-[rgba(var(--arc-blue),0.50)]">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-[rgba(var(--arc-blue),0.50)]">
        <div className="flex items-center gap-3">
          <Activity className="w-6 h-6 text-cyan-400" />
          <h2 className="text-xl font-bold violett-gradient-text text-drop-shadow">
            HiveCore Control
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-400' : 'bg-red-400'}`} />
          <span className="text-sm text-slate-400">
            {connected ? 'Connected' : 'Disconnected'}
          </span>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="flex-1 overflow-auto p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {/* Active Workers */}
          <div className="glass-dark rounded-xl p-4 bg-gradient-to-br from-[rgba(var(--arc-blue),0.60)] to-[rgba(var(--arc-navy),0.40)] border border-[rgba(var(--arc-blue),0.30)]">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-4 h-4 text-cyan-400" />
              <span className="text-sm text-slate-400">Active Workers</span>
            </div>
            <div className="text-3xl font-bold violett-gradient-text">
              {state.workers}
            </div>
          </div>

          {/* Queue Depth */}
          <div className="glass-dark rounded-xl p-4 bg-gradient-to-br from-[rgba(var(--arc-blue),0.60)] to-[rgba(var(--arc-navy),0.40)] border border-[rgba(var(--arc-blue),0.30)]">
            <div className="flex items-center gap-2 mb-2">
              <Database className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-slate-400">Queue Depth</span>
            </div>
            <div className="text-3xl font-bold violett-gradient-text">
              {state.queueDepth}
            </div>
          </div>

          {/* CPU Usage */}
          <div className="glass-dark rounded-xl p-4 bg-gradient-to-br from-[rgba(var(--arc-blue),0.60)] to-[rgba(var(--arc-navy),0.40)] border border-[rgba(var(--arc-blue),0.30)]">
            <div className="flex items-center gap-2 mb-2">
              <Cpu className="w-4 h-4 text-orange-400" />
              <span className="text-sm text-slate-400">CPU Usage</span>
            </div>
            <div className="text-3xl font-bold violett-gradient-text">
              {formatPercent(cpuPercent, 1)}
            </div>
            {metrics && (
              <div className="text-xs text-slate-400 mt-2">
                Load avg (1m/5m/15m):
                {' '}
                {formatNumber(metrics.cpu?.loadAverage?.['1m'])}/
                {formatNumber(metrics.cpu?.loadAverage?.['5m'])}/
                {formatNumber(metrics.cpu?.loadAverage?.['15m'])}
              </div>
            )}
          </div>

          {/* Memory Usage */}
          <div className="glass-dark rounded-xl p-4 bg-gradient-to-br from-[rgba(var(--arc-blue),0.60)] to-[rgba(var(--arc-navy),0.40)] border border-[rgba(var(--arc-blue),0.30)]">
            <div className="flex items-center gap-2 mb-2">
              <HardDrive className="w-4 h-4 text-emerald-400" />
              <span className="text-sm text-slate-400">Memory Usage</span>
            </div>
            <div className="text-3xl font-bold violett-gradient-text">
              {formatPercent(effectiveMemoryPercent, 1)}
            </div>
            {metrics && (
              <div className="text-xs text-slate-400 mt-2">
                {formatMegabytes(metrics.memory?.usedMegabytes)} / {formatMegabytes(metrics.memory?.totalMegabytes)}
              </div>
            )}
          </div>
        </div>

        {metricsError && (
          <div className="mb-4 px-4 py-3 rounded-lg border border-red-500/40 bg-red-500/10 text-sm text-red-300">
            Gateway telemetry error: {metricsError}
          </div>
        )}

        {/* BuildStacks Table */}
        <div className="glass-dark rounded-xl p-4 bg-gradient-to-br from-[rgba(var(--arc-blue),0.60)] to-[rgba(var(--arc-navy),0.40)] border border-[rgba(var(--arc-blue),0.30)] mb-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold violett-gradient-text">BuildStacks</h3>
            <button
              data-key={widgetKey}
              data-state={currentState}
              onClick={handleRefresh}
              onPointerDown={handlePointerDown}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerLeave}
              aria-pressed={currentState === 'pressed' || currentState === 'pending'}
              aria-busy={currentState === 'pending'}
              aria-label="Refresh scene data"
              disabled={currentState === 'disabled'}
              className="px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/40 text-cyan-400 hover:text-cyan-300 rounded-lg border border-cyan-500/30 hover:border-cyan-500/50 shadow-lg hover:shadow-cyan-500/20 flex items-center"
            >
              <span>Refresh</span>
              <span className="abda-btn-indicator">
                {showSpinner && <span className="abda-btn-spinner" />}
              </span>
            </button>
          </div>
          
          {state.buildstacks.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              No BuildStacks running
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700/50">
                    <th className="text-left py-2 px-3 text-sm text-slate-400">Name</th>
                    <th className="text-left py-2 px-3 text-sm text-slate-400">Status</th>
                    <th className="text-left py-2 px-3 text-sm text-slate-400">Last Run</th>
                  </tr>
                </thead>
                <tbody>
                  {state.buildstacks.map((stack, idx) => (
                    <tr key={idx} className="border-b border-slate-700/30">
                      <td className="py-2 px-3 text-slate-200">{stack.name}</td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          {stack.status === 'running' ? (
                            <CheckCircle className="w-4 h-4 text-green-400" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-yellow-400" />
                          )}
                          <span className="text-slate-300">{stack.status}</span>
                        </div>
                      </td>
                      <td className="py-2 px-3 text-slate-400 text-sm">
                        <div className="flex items-center gap-2">
                          <Clock className="w-3 h-3" />
                          {stack.lastRun}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="glass-dark rounded-xl p-4 bg-gradient-to-br from-[rgba(var(--arc-blue),0.60)] to-[rgba(var(--arc-navy),0.40)] border border-[rgba(var(--arc-blue),0.30)]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold violett-gradient-text">Gateway Telemetry</h3>
            <div className="text-xs text-slate-400">
              {metrics
                ? `Sampled ${lastSample?.toLocaleTimeString()}`
                : metricsLoading
                  ? 'Collecting metrics…'
                  : 'No telemetry available'}
            </div>
          </div>

          {metrics ? (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <div className="rounded-lg border border-[rgba(var(--arc-blue),0.40)] bg-[rgba(var(--arc-navy),0.40)] p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Server className="w-4 h-4 text-cyan-400" />
                  <span className="text-sm text-slate-300">Runtime</span>
                </div>
                <dl className="space-y-2 text-sm text-slate-300">
                  <div className="flex justify-between">
                    <dt className="text-slate-400">Hostname</dt>
                    <dd>{metrics.environment?.hostname || '—'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-400">Platform</dt>
                    <dd>{metrics.environment?.platform || '—'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-400">Version</dt>
                    <dd className="text-right max-w-[60%] truncate" title={metrics.environment?.platformVersion || undefined}>
                      {metrics.environment?.platformVersion || '—'}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-400">Uptime</dt>
                    <dd>{formatUptime(metrics.uptimeSeconds)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-400">Sampled</dt>
                    <dd>{lastSample?.toLocaleString() || '—'}</dd>
                  </div>
                </dl>
              </div>

              <div className="rounded-lg border border-[rgba(var(--arc-blue),0.40)] bg-[rgba(var(--arc-navy),0.40)] p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Gauge className="w-4 h-4 text-orange-400" />
                  <span className="text-sm text-slate-300">CPU Load</span>
                </div>
                <dl className="space-y-2 text-sm text-slate-300">
                  <div className="flex justify-between">
                    <dt className="text-slate-400">Logical cores</dt>
                    <dd>{metrics.cpu?.count ?? '—'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-400">Normalized (1m)</dt>
                    <dd>{formatPercent(cpuPercent, 1)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-400">Load (1m)</dt>
                    <dd>{formatNumber(metrics.cpu?.loadAverage?.['1m'])}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-400">Load (5m)</dt>
                    <dd>{formatNumber(metrics.cpu?.loadAverage?.['5m'])}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-400">Load (15m)</dt>
                    <dd>{formatNumber(metrics.cpu?.loadAverage?.['15m'])}</dd>
                  </div>
                </dl>
              </div>

              <div className="rounded-lg border border-[rgba(var(--arc-blue),0.40)] bg-[rgba(var(--arc-navy),0.40)] p-4">
                <div className="flex items-center gap-2 mb-3">
                  <HardDrive className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm text-slate-300">Memory</span>
                </div>
                <dl className="space-y-2 text-sm text-slate-300">
                  <div className="flex justify-between">
                    <dt className="text-slate-400">Usage</dt>
                    <dd>{formatPercent(memoryPercent ?? effectiveMemoryPercent, 1)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-400">Used</dt>
                    <dd>{formatMegabytes(metrics.memory?.usedMegabytes)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-400">Available</dt>
                    <dd>{formatMegabytes(metrics.memory?.availableMegabytes)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-400">Total</dt>
                    <dd>{formatMegabytes(metrics.memory?.totalMegabytes)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-400">Process RSS</dt>
                    <dd>{formatMegabytes(metrics.process?.rssMegabytes)}</dd>
                  </div>
                </dl>
              </div>

              <div className="rounded-lg border border-[rgba(var(--arc-blue),0.40)] bg-[rgba(var(--arc-navy),0.40)] p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Terminal className="w-4 h-4 text-sky-300" />
                  <span className="text-sm text-slate-300">Process</span>
                </div>
                <dl className="space-y-2 text-sm text-slate-300">
                  <div className="flex justify-between">
                    <dt className="text-slate-400">PID</dt>
                    <dd>{metrics.process?.pid ?? '—'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-400">CPU (user)</dt>
                    <dd>{formatNumber(metrics.process?.cpuTimes?.user, 3)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-400">CPU (system)</dt>
                    <dd>{formatNumber(metrics.process?.cpuTimes?.system, 3)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-400">Timestamp</dt>
                    <dd>{lastSample?.toLocaleString() || '—'}</dd>
                  </div>
                </dl>
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-400">
              {metricsLoading
                ? 'Gathering telemetry from the HiveCore gateway…'
                : 'System telemetry is not available. Check the gateway connection or configuration.'}
            </div>
          )}
        </div>

        {/* Last Update */}
        {state.lastUpdate && (
          <div className="text-center text-sm text-slate-500">
            Last updated: {new Date(state.lastUpdate).toLocaleTimeString()}
          </div>
        )}
      </div>
    </div>
  );
}
